import React from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import _ from 'lodash';
import classnames from 'classnames';

import t from '../../../lib/I18n';
import trainTime from '../../../lib/TrainTime';

import Icon from '../../general/Icon';
import Price from '../../general/Price';

const OPEN_TICKET_DATE_FORMAT = 'ddd Do';

export const compareTrips = (orderFare, ticketFare) => {
    let areEqual = false;

    let orderFareMap = new Map(orderFare.outwardValidity ? orderFare.outwardValidity : orderFare.outwardJourney);
    let ticketFareMap = new Map(ticketFare.outwardValidity ? ticketFare.outwardValidity : ticketFare.outwardJourney);
    let orderFareValidity = {
        from: orderFareMap.get('validFrom') || orderFareMap.get('from'),
        until: orderFareMap.get('validTo') || orderFareMap.get('until')
    };
    let ticketFareValidity = {
        from: ticketFareMap.get('validFrom') || ticketFareMap.get('from'),
        until: ticketFareMap.get('validTo') || ticketFareMap.get('until')
    };

    if (orderFare.origin
        && ticketFare.origin
        && orderFare.origin.nlc === ticketFare.origin.nlc
        && orderFare.destination.nlc === ticketFare.destination.nlc
        && orderFare.ticketType.code === ticketFare.ticketType.code
        && orderFareValidity.from.isSame(ticketFareValidity.from, 'day')
        && orderFareValidity.until.isSame(ticketFareValidity.until, 'day')) {
            areEqual = true;
    }

    return areEqual;
};

const OpenTicketSelector = ({
    currentTicket = null,
    isExpanded = false,
    loadingTrips = [],
    onClick = _.noop,
    onTicketSelect = _.noop,
    order = null,
    tickets = []
}) => {
    const isSelected = fare => {
        let isTicketLoading = false;
        let isTicketSelected = false;
        let orderTrip = null;

        if (order) {
            order.trips.map(trip => {
                if (trip && trip.fares && trip.fares.return) {
                    if (compareTrips(trip.fares.return, fare)) {
                        orderTrip = trip;
                        isTicketSelected = true;
                    }
                }
            });
        }

        if (loadingTrips) {
            loadingTrips.map(trip => {
                if (compareTrips(trip, fare)) {
                    isTicketLoading = true;
                }
            });
        }

        return { isTicketSelected, isTicketLoading, orderTrip };
    };

    const getFromValidDate = ticket => trainTime(ticket.outwardValidity.from).format(OPEN_TICKET_DATE_FORMAT);
    const getUntilValidDate = ticket => trainTime(ticket.outwardValidity.until).format(OPEN_TICKET_DATE_FORMAT);

    const sortedTickets = tickets
        ? [...tickets.toJS()
            .filter(ticket => ticket.outwardValidity && ticket.ticketType.code === `OG${currentTicket}`)]
            .sort((a, b) => a.outwardValidity.from.isAfter(b.outwardValidity.from) ? 1 : -1)
        : [];

    return (<div
            className={ classnames(
                'open-ticket-selector',
                {
                    'open-ticket-selector--expanded': isExpanded
                }) }
        >
            <div className="open-ticket-selector__header" onClick={ onClick }>
                <div className="open-ticket-selector__header__titles">
                    <h4 className="open-ticket-selector__header__title">{ t(`openGolf.tickets.day${currentTicket}`) }</h4>
                    <h5 className="open-ticket-selector__header__subtitle">{ t('openGolf.selector.subtitle') }</h5>
                </div>
                <div>
                    <Icon icon="chevron" className={`open-ticket-selector__header__icon otrl-rotate-${isExpanded ? 270 : 90}`} />
                </div>
            </div>
            <table className="open-ticket-selector__tickets">
                <tbody>
                    { isExpanded && sortedTickets && sortedTickets.map(ticket => {
                        const { isTicketSelected, isTicketLoading, orderTrip } = isSelected(ticket);
                        const appliedRailcards = ticket.tickets.map(cur => cur.railcard).filter(cur => !!cur);
                        const hasRailcards = appliedRailcards.length > 0;
                        const isPriceReduced = ticket.totalPrice < ticket.originalTotalPrice;
                        return (
                            <React.Fragment>
                                <tr className={ classnames('open-ticket-selector__tickets__ticket', { 'open-ticket-selector__tickets__ticket--no-railcards': hasRailcards }) } key={ticket.id}>
                                    <td className="open-ticket-selector__tickets__ticket__date">
                                        { getFromValidDate(ticket) === getUntilValidDate(ticket) ? getFromValidDate(ticket) : `${getFromValidDate(ticket)} - ${getUntilValidDate(ticket)}` }
                                    </td>
                                    <td className="open-ticket-selector__tickets__ticket__price">
                                        { isPriceReduced
                                            ? <React.Fragment>
                                                <Price price={ ticket.totalPrice } /><span className="open-ticket-selector__tickets__ticket__price__reduced"><br/><Price price={ ticket.originalTotalPrice } /></span>
                                              </React.Fragment>
                                            : <Price price={ ticket.totalPrice } /> }
                                    </td>
                                    <td className="open-ticket-selector__tickets__ticket__cta">
                                        <div
                                            className={ classnames('btn-compact open-ticket-selector__tickets__ticket__cta__button', { 'open-ticket-selector__tickets__ticket__cta__button--remove': isTicketSelected }) }
                                            onClick={ !isTicketLoading ? onTicketSelect(ticket.id, orderTrip) : _.noop }
                                        >{ isTicketLoading
                                            ? <span className="otrl circle-spinner otrl circle-spinner--small" />
                                            : isTicketSelected
                                                ? <React.Fragment><Icon icon="bin" /> { t('openGolf.ticket.remove') }</React.Fragment>
                                                : t('openGolf.ticket.add') }</div>
                                    </td>
                                </tr>
                                { hasRailcards && <tr className="open-ticket-selector__tickets__ticket-railcards">
                                    <td colSpan="3" className="open-ticket-selector__tickets__ticket-railcards__column">
                                        { appliedRailcards.map(railcard => <span className="open-ticket-selector__tickets__ticket-railcards__column__railcard">{ railcard.name }</span>)}
                                    </td>
                                </tr> }
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

OpenTicketSelector.propTypes = ({
    currentTicket: PropTypes.number,
    isExpanded: PropTypes.bool,
    loadingTrips: PropTypes.object,
    onClick: PropTypes.func,
    onTicketSelect: PropTypes.func,
    order: PropTypes.object,
    tickets: PropTypes.object
});

export default OpenTicketSelector;



// WEBPACK FOOTER //
// ./src/components/widget/openTicketSelection/OpenTicketSelector.js